import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, Download, Instagram, Linkedin, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import ResponseBadge from "@/components/ResponseBadge";
import { COMPANY_PROFILE_PDF, SOCIAL_LINKS } from "@/lib/media";

const CONTACT_BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Contact", path: "/contact" },
];

const HQ = {
  city: "Dubai",
  country: "United Arab Emirates",
  address: "Office 1204, The Exchange Tower\nBusiness Bay, Dubai, UAE",
  phone: "+971 58 179 9090",
  email: "hello@trawelldmc.com",
  isHq: true,
};

const OFFICES = [
  {
    city: "Singapore",
    country: "Singapore",
    address: "10 Marina Boulevard, #22-01\nMarina Bay Financial Centre\nSingapore 018983",
  },
  {
    city: "Hong Kong",
    country: "Hong Kong SAR",
    address: "Suite 3101, Two International Finance Centre\n8 Finance Street, Central, Hong Kong",
  },
  {
    city: "Bangkok",
    country: "Thailand",
    address: "88/8 Silom Road, Bangrak\nBangkok 10500, Thailand",
  },
  {
    city: "Ho Chi Minh City",
    country: "Vietnam",
    address: "12th Floor, Saigon Centre Tower 1\n65 Le Loi Street, Ben Nghe Ward\nHo Chi Minh City, Vietnam",
  },
  {
    city: "Kuala Lumpur",
    country: "Malaysia",
    address: "Level 18, Menara Prestige\n1 Jalan Pinang, KLCC\nKuala Lumpur 50450, Malaysia",
  },
  {
    city: "Manila",
    country: "Philippines",
    address: "32/F Yuchengco Tower, RCBC Plaza\nAyala Avenue, Makati City\nManila 1226, Philippines",
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Seo
        path="/contact"
        title="Contact Trawelldmc — 7 Global Offices · Dubai HQ"
        description="Reach Trawelldmc — Dubai HQ plus six regional offices across Singapore, Hong Kong, Bangkok, Ho Chi Minh City, Kuala Lumpur and Manila. hello@trawelldmc.com · +971 58 179 9090."
        breadcrumb={CONTACT_BREADCRUMB}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <div className="overline mb-5">Contact</div>
          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-[#0B192C]">
            Dubai HQ. <br />
            <span className="text-[#FF5238]">Seven offices.</span> Asia-wide operations.
          </h1>
          <div className="mt-8"><ResponseBadge /></div>
        </div>

        {/* HQ + QUICK CONTACTS */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5 space-y-8">
            <div className="border-l-2 border-[#FF5238] pl-5" data-testid="contact-hq">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-[#FF5238]" />
                <div className="overline">Head Office · Dubai</div>
              </div>
              <div className="font-heading text-lg text-[#0B192C] whitespace-pre-line">{HQ.address}</div>
              <div className="text-sm text-slate-500 mt-1">{HQ.country}</div>
            </div>
            <div className="border-l-2 border-[#FF5238] pl-5">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-[#FF5238]" />
                <div className="overline">Sales</div>
              </div>
              <a href={`mailto:${HQ.email}`} className="font-heading text-lg text-[#0B192C] hover:text-[#FF5238] transition-colors" data-testid="contact-email">
                {HQ.email}
              </a>
            </div>
            <div className="border-l-2 border-[#FF5238] pl-5">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="h-4 w-4 text-[#FF5238]" />
                <div className="overline">Phone & WhatsApp</div>
              </div>
              <a href={`tel:${HQ.phone.replace(/\s/g, "")}`} className="font-heading text-lg text-[#0B192C] hover:text-[#FF5238] transition-colors" data-testid="contact-phone">
                {HQ.phone}
              </a>
            </div>
            <div className="border-l-2 border-[#FF5238] pl-5">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-[#FF5238]" />
                <div className="overline">Hours</div>
              </div>
              <div className="font-heading text-lg text-[#0B192C]">
                Sun–Thu 9:00 – 19:00 GST
                <div className="text-sm text-slate-500 mt-1 font-sans">24/7 on-ground support worldwide</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-[#FDFCF8] border border-slate-200 p-8 md:p-12">
            <div className="overline mb-3">We'd love to hear from you</div>
            <h2 className="font-heading text-3xl font-light tracking-tight text-[#0B192C] mb-6">
              The fastest way to start is our inquiry form.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Share your brief — destination, dates, pax, budget — and a dedicated consultant will
              return a proposal within 48 hours. Agents can also log in to the dashboard to track all
              active quotes.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/inquiry" className="btn-primary" data-testid="contact-inquiry-cta">Submit inquiry</Link>
              <Link to="/quote" className="btn-secondary" data-testid="contact-quote-cta">Get AI first quote</Link>
            </div>
          </div>
        </div>
      </section>

      {/* GLOBAL OFFICES */}
      <section className="bg-[#0B192C] text-white" data-testid="contact-global-offices">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Global Offices</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Seven offices. <br />
              <span className="text-[#FF5238]">One accountable team.</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed">
              Each destination is operated by a local on-ground team — visa support, hotel
              contracting, transport and 24/7 ops, in-region.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {OFFICES.map((o) => (
              <div key={o.city} className="border-t border-white/15 pt-6" data-testid={`office-${o.city.toLowerCase().replace(/ /g, "-")}`}>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-[#FF5238]" />
                  <div className="overline !text-white/80">{o.country}</div>
                </div>
                <div className="font-heading text-2xl font-medium tracking-tight text-white">{o.city}</div>
                <p className="text-sm text-white/70 mt-3 whitespace-pre-line leading-relaxed">{o.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPANY PROFILE DOWNLOAD + SOCIAL */}
      <section className="bg-[#FDFCF8] border-y border-slate-200" data-testid="contact-resources">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Resources</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Download the <br />
              <span className="text-[#FF5238]">Trawelldmc company profile.</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              A 15-page PDF covering our services, destinations, case studies, and operating
              philosophy. Share it with stakeholders, RFP committees, and finance teams.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={COMPANY_PROFILE_PDF}
                download="trawelldmc-company-profile.pdf"
                className="btn-primary inline-flex"
                data-testid="contact-profile-download"
              >
                Download Company Profile <Download className="h-4 w-4" />
              </a>
              <a
                href={COMPANY_PROFILE_PDF}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="contact-profile-view"
              >
                View in browser <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overline mb-4">Follow Trawelldmc</div>
            <p className="text-slate-600 mb-6 leading-relaxed">
              See the work — gala dinners, conference reveals, behind-the-scenes operations from
              across our seven destinations.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-5 py-3 bg-[#0B192C] text-white hover:bg-[#FF5238] transition-colors"
                data-testid="social-instagram"
                aria-label="Follow Trawelldmc on Instagram"
              >
                <Instagram className="h-5 w-5" />
                <span className="font-heading text-sm font-medium">Instagram</span>
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-5 py-3 bg-[#0B192C] text-white hover:bg-[#FF5238] transition-colors"
                data-testid="social-linkedin"
                aria-label="Connect with Trawelldmc on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
                <span className="font-heading text-sm font-medium">LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
